/**
 * 상호작용 오브젝트의 시각화 + 서버 상태 반영.
 * 이 파일은 상태를 '읽어서 보여주기만' 한다. 상태를 바꾸는 건 오직 서버.
 */

import * as THREE from 'three';
import { WALL_T, DOORWAYS, type PropDef } from '../../shared/level';
import { ep } from './episodeData';
import type { WorldState } from '../../shared/protocol';
import { setRoomLight, type BuiltWorld } from './world';
import { buildPiece, PIECE_BY_ID, openSpecFor } from './furniture';

export interface PropView {
  /** 이 프롭이 선 바닥 높이. 되돌릴 때 쓴다 */
  floorY?: number;
  def: PropDef;
  group: THREE.Group;
  /** 조준 판정용 메시들 */
  hit: THREE.Object3D[];
  /** 애니메이션 대상 */
  moving?: THREE.Object3D;
  /** 조각이 제 안에 안고 있는 움직이는 부품들 (`Piece.openable`) — 여럿이 동시에 움직인다 */
  openParts?: { obj: THREE.Object3D; swing?: number; axis?: 'x' | 'y' | 'z'; slide?: THREE.Vector3; gather?: number; base: THREE.Vector3 }[];
  target: number;
  current: number;
  /** 마지막으로 트랜스폼에 반영한 current. 같으면 다시 쓰지 않는다 */
  applied?: number;
  collider?: { minX: number; maxX: number; minZ: number; maxZ: number; enabled: boolean };
  /** 조준 하이라이트로 칠하는 재질 — **이 물건만의 복제본**. 처음 조준될 때 `ownMaterials` 가 만든다 */
  highlightable: THREE.MeshStandardMaterial[];
}

/** 조준 판정 전용 레이어 — 카메라는 이 레이어를 그리지 않고, 레이캐스터만 본다 */
export const AIM_LAYER = 1;

/**
 * 조준 판정용 상자.
 * 레이어를 갈라두면 렌더 패스에서 완전히 빠지면서(=draw call 0) 조준은 확실히 동작한다.
 * (투명 재질로 두면 실제로 그려지고, visible=false 는 조준 동작이 three 버전에 의존한다.)
 */
function hitBox(w: number, h: number, d: number) {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    // 레이어만 갈라 두는 것으로는 부족했다. GTAO 같은 프리패스는 카메라 레이어를
    // 무시하고 씬을 다시 그리기 때문에, 이 상자가 **흰 판으로 화면에 찍혔다**
    // (마을 길 한복판에 뜬 그 사각형이 이것이다).
    // colorWrite/depthWrite 를 꺼 두면 어느 패스에서 그려지든 아무것도 안 남긴다.
    // 레이캐스트는 재질과 무관하게 동작한다.
    new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false }),
  );
  m.layers.set(AIM_LAYER);
  return m;
}

/**
 * **이 물건의 메시를 제 몫의 재질로 바꿔 끼운다** (2026-09-25).
 *
 * 조각 재질은 슬롯·조각 종류마다 하나를 여러 가구가 나눠 쓴다. 조준 하이라이트가 그걸 그대로 칠해서
 * 같은 재질을 쓰는 다른 가구까지 같이 빛났다(참나무 상자를 조준하면 마을의 참나무가 전부).
 * 복제본은 셰이더 프로그램·텍스처를 원본과 공유하므로 싸다. 처음 조준될 때만 만들고,
 * 그 뒤에 붙은 메시(GLB 가 늦게 도착한 것)는 다음 조준 때 마저 바꾼다.
 * `clone()` 은 `onBeforeCompile`·`customProgramCacheKey` 를 안 옮긴다 — 직접 옮겨야 시신 피부·눈 쌓임 셰이더가 산다.
 */
function ownMaterials(v: PropView) {
  const mine = new Set(v.highlightable);
  const copies = new Map<THREE.Material, THREE.MeshStandardMaterial>();
  v.group.traverse((c) => {
    if (!(c instanceof THREE.Mesh) || !(c.material instanceof THREE.MeshStandardMaterial) || mine.has(c.material)) return;
    const src = c.material;
    let cp = copies.get(src);
    if (!cp) {
      cp = src.clone();
      if (Object.prototype.hasOwnProperty.call(src, 'onBeforeCompile')) cp.onBeforeCompile = src.onBeforeCompile;
      if (Object.prototype.hasOwnProperty.call(src, 'customProgramCacheKey')) cp.customProgramCacheKey = src.customProgramCacheKey;
      // WebGPU 판 개조(gpu.ts onGpu 가 얹은 colorNode 등)도 clone 이 안 옮긴다
      for (const k of Object.keys(src)) if (k.endsWith('Node')) (cp as unknown as Record<string, unknown>)[k] = (src as unknown as Record<string, unknown>)[k];
      copies.set(src, cp);
      mine.add(cp);
      v.highlightable.push(cp);
    }
    c.material = cp;
  });
}

export function buildProps(
  built: BuiltWorld,
  renderer: THREE.WebGLRenderer,
  /**
   * 그 자리의 바닥 높이. **기단이 생기면서 필요해졌다** — 프롭 좌표는
   * 「바닥에서 얼마」라는 뜻인데, 여태 절대 y 로 읽어서 기단 위 방의 문과
   * 물건이 전부 40cm 파묻혔다.
   */
  floorAt?: (x: number, z: number) => number,
): Map<string, PropView> {
  const views = new Map<string, PropView>();
  pendingAttach.clear(); pendingRoot = built.root;

  for (const def of ep().PROPS) {
    const group = new THREE.Group();
    const fy = floorAt?.(def.pos[0], def.pos[2]) ?? 0;
    group.position.set(def.pos[0], fy + def.pos[1], def.pos[2]);
    const view: PropView = {
      def,
      group,
      floorY: fy,
      hit: [],
      /* **처음부터 열려 있는 것은 열린 채로 짓는다** — 시신이 이미 발견된 붙박이장 문짝(`init.open`).
       * 여태 0 으로 지어 에디터에선 닫힌 문 사이로 시신 다리가 튀어나왔다(2026-09-15, Chris).
       * 게임은 서버 상태가 곧 덮어쓰므로 첫 프레임 애니메이션만 없어진다. */
      target: def.init?.open ? 1 : 0,
      current: def.init?.open ? 1 : 0,
      highlightable: [],
    };

    /* 에디터 크기 조절. **축별 배율(scale3)이 무시되고 있었다** — 벽처럼 한 축만 늘린
     * 프롭이 화면에서는 그대로였다(2026-09-11). 축별이 있으면 그걸 먼저 쓴다. */
    if (def.scale3) view.group.scale.set(def.scale3[0], def.scale3[1], def.scale3[2]);
    else if (def.scale && def.scale !== 1) view.group.scale.setScalar(def.scale);
    if (def.tilt) { view.group.rotation.x = def.tilt[0]; view.group.rotation.z = def.tilt[1]; }   // 에디터 기울기
    switch (def.kind) {
      case 'door':
        buildDoor(def, view, renderer);
        break;
      case 'drawer':
        buildDrawer(def, view);
        break;
      /* **`case 'lid':` 를 지우지 마라.** 2026-09-11 에 `buildLid` 한 줄을 `attachToPart` 로
       * 바꾸면서 바로 위의 case 레이블까지 같이 지웠다(7a1f7e2). 그 뒤로 게임의 **모든 덮개
       * 프롭이 죽었다** — 쓰레기통·서랍·상자 플랩·붙박이장 전부. 아래 두 줄은 `case 'drawer'`
       * 의 `break;` 뒤라 영영 실행되지 않는 죽은 코드였는데, 타입도 린트도 잡지 않았고
       * 카탈로그 검수는 조각만 보므로 하루를 통째로 놓쳤다(Chris 「열리냐」). */
      case 'lid':
        // 이미 놓인 조각의 부품을 맡는 프롭이면 조각을 다시 그리지 않는다
        if (!attachToPart(def, view, built.root)) {
          // GLB 조각은 모델이 나중에 붙는다 — 부품이 아직 없으면 기다렸다가 다시 붙인다(2026-09-15). 붙을 조각이 없는 것만 절차 덮개
          if (def.attach && def.part) { const list = pendingAttach.get(def.attach) ?? []; list.push(view); pendingAttach.set(def.attach, list); }
          else buildLid(def, view, renderer);
        }
        break;
      case 'pickup':
        buildPickup(def, view);
        break;
      case 'switch':
        buildSwitch(def, view);
        break;
      case 'exam':
        def.id === 'body' ? buildBody(def, view) : buildExam(def, view, renderer);
        break;
      case 'safe':
        buildSafe(def, view);
        break;
      case 'board':
        buildBoard(view);
        break;
    }

    for (const h of view.hit) h.userData.propId = def.id;
    built.root.add(group);
    views.set(def.id, view);
    if (view.collider) built.colliders.push(view.collider);
  }

  return views;
}

// --- 종류별 조립 ---

function buildDoor(def: PropDef, v: PropView, renderer: THREE.WebGLRenderer) {
  const rot = def.rot ?? 0;
  const dir = new THREE.Vector3(Math.cos(rot), 0, Math.sin(rot));
  const hinge = new THREE.Group();
  hinge.position.y = 0;
  v.group.add(hinge);
  hinge.rotation.y = -rot;
  /**
   * 미닫이가 밀려가는 방향과 출발 자리.
   *
   * `position` 은 **부모 좌표계** 기준이라 그룹 자신의 회전과 무관하다.
   * 그걸 잊고 `position.x` 를 바꿨더니 문이 벽을 따라가는 게 아니라 월드 X 축으로,
   * 즉 보는 사람 기준 「앞으로」 밀려 나왔다.
   *
   * 벽을 따라가는 방향은 `dir`(문 폭 방향)이다. 그것만 기억해 두면 된다.
   */
  hinge.userData.slideDir = dir.clone();

  // 문짝은 카탈로그 조각이다 (client/src/furniture/arch.ts 의 door_panel).
  // **움직이는 부품이라 굽지 않는다** — 힌지 그룹에 그대로 얹는다. 조각은 슬롯당
  // 메시 하나로 합쳐 나오므로 문 하나가 3 draw call 이다 (예전에는 판·손잡이·패널선
  // 3메시 + 재질 3개를 문마다 새로 만들었다 — 13개 문에 재질 39개).
  /**
   * 사건마다 문이 다르다 — 사흘은 90년대 목문(여닫이), 초치성은 창호(미닫이).
   * 미닫이는 벽을 따라 미끄러지므로 힌지에 얹되 **회전이 아니라 이동**한다
   * (아래 `case 'door'` 참고).
   */
  const slide = ep().doorStyle === 'slide';
  const piece = PIECE_BY_ID[slide ? 'door_slide_hanok' : 'door_panel'];
  // **문짝 높이는 조각이 정한다.** 2.18 로 박아 두었더니 창호를 2.02 로 줄인
  // 뒤에도 그 값으로 앉혀서 문이 바닥에 파묻혔다
  /**
   * **문짝 높이도 그 문구멍이 정한다.** 폭과 같은 이야기다.
   *
   * 폭만 `size[0]` 으로 받고 높이는 조각 기본값(2.02)을 쓰고 있었다. 그런데
   * 구멍은 대부분 2.1 이고 **대문만 2.3** 이다 — 대문 위로 28cm 가 뻥 뚫려
   * 마당에서 담 너머가 들여다보였다. 나머지 문도 8cm 씩 벌어 있었다.
   */
  const DOOR_H = def.size?.[1] ?? piece.dims.h ?? 2.18;
  /**
   * 문짝 폭은 **그 문구멍**이 정한다.
   *
   * 구멍 폭이 0.9 부터 1.6 까지 제각각인데 문짝은 하나뿐이라, 좁은 데는 문이
   * 삐져나오고 넓은 데는 틈이 벌었다. `PropDef.size[0]` 으로 문마다 준다 —
   * 안 주면 조각 기본값이다.
   */
  /* **구멍에서 폭을 찾는다** (2026-09-14, Chris: 「문들 지금 손잡이가 다 안 보인다, 문 자체
   * 리사이징 들어간 거 맞냐」). 구워 넣는 문짝은 개구부에서 폭을 뽑게 고쳤는데 **여닫는
   * 문(이 함수)은 그대로 조각 기본값 1.94** 였다. 사흘 v7 구멍은 1.0m 라 손잡이(중심에서
   * 0.78m)가 통째로 벽 속에 묻혔다. 경첩 자리 0.8m 안의 개구부를 찾아 그 폭을 쓴다.
   * `size[0]` 을 명시한 문(초치성 대문 등)은 그대로 존중한다. */
  const nearOpening = DOORWAYS.find((o) => Math.hypot(o.x - def.pos[0], o.z - def.pos[2]) < 0.8);
  const fromOpening = nearOpening?.half !== undefined ? nearOpening.half * 2 - 0.08 : undefined;
  const ownHalf = (def as { half?: number }).half;
  const DOOR_W = def.size?.[0] ?? (ownHalf !== undefined ? ownHalf * 2 - 0.08 : fromOpening) ?? piece.dims.w ?? 1.0;

  /**
   * **넓은 문은 두 짝이다.**
   *
   * 한옥에서 한 짝으로 미는 문은 한 사람 지나갈 폭까지다. 본당처럼 넓은 문은
   * 가운데서 갈라져 양쪽으로 물러나는 두 짝(쌍여닫이 창호)이고, 그 벌어지는
   * 동작 자체가 「굿을 여는 문」의 인상이다.
   *
   * 1.25 를 경계로 잡았다 — 그보다 넓은 문짝은 한 사람 몸보다 커서 혼자
   * 밀기에 안 어울린다.
   */
  const twin = slide && DOOR_W > 1.25;
  const leafW = twin ? DOOR_W / 2 : DOOR_W;
  const { group: leaf } = buildPiece(piece, renderer, { w: leafW, h: DOOR_H });

  /**
   * **`def.pos` 는 경첩 자리다.** 문 중심이 아니다.
   *
   * 여닫이는 그 자리에 축이 있으니 맞다 — 힌지를 폭만큼 뒤로 물리고 문짝을
   * 앞으로 내면 문이 구멍을 덮는다.
   *
   * 미닫이는 축이 없다. 문짝이 **구멍을 통째로 채우고** 옆으로 빠지므로,
   * 문 중심이 구멍 중심(= 경첩 자리에서 폭 절반만큼 옆)에 와야 한다.
   * 그걸 안 맞춰서 창호가 구멍 옆 벽에 걸쳐 서 있었다 — 「열려 있다는데
   * 통과가 안 되고 모습이 다르다」가 그 증상이었다.
   */
  /**
   * **문은 바닥에 선다.**
   *
   * 예전에는 그룹을 1.15 로 올리고 힌지에서 `-def.pos[1]` 을 빼고 문짝을
   * 다시 `-H/2` 내렸다 — 세 보정이 겹쳐서 밑면이 바닥보다 86cm 아래로
   * 빠졌고, 문구멍의 위쪽 절반이 뻥 뚫려 있었다.
   *
   * 조각 원점이 밑면이므로 **그룹을 바닥에 두고 문짝을 0 에 놓으면** 끝난다.
   */
  hinge.position.copy(dir).multiplyScalar(slide ? DOOR_W / 2 : -1);
  hinge.position.y = 0;
  /**
   * **출발 자리는 여기서 기억한다.**
   *
   * 위쪽(그룹 만들 때)에서 `hinge.position.clone()` 을 찍고 있었는데, 그때는
   * 아직 (0,0,0) 이었다. 매 프레임 애니메이션이 그 값으로 되돌리니 한 짝 문이
   * 구멍 중심이 아니라 **경첩 자리**로 밀려 반만 닫혔다.
   *
   * 두 짝은 짝을 각자 옮기느라 이 값을 안 써서 멀쩡했다 — 그래서 한 짝 문
   * 셋만 증상이 남았다.
   */
  hinge.userData.slideBase = hinge.position.clone();
  leaf.position.set(slide ? (twin ? -leafW / 2 : 0) : 1.0, 0, 0);
  /** 두 짝이면 반대쪽 한 짝을 더 만든다. 열 때 서로 반대로 물러난다 */
  let leaf2: THREE.Group | null = null;
  if (twin) {
    leaf2 = buildPiece(piece, renderer, { w: leafW, h: DOOR_H }).group;
    leaf2.position.set(leafW / 2, 0, 0);
    leaf2.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.castShadow = true;
        o.receiveShadow = true;
      }
    });
    hinge.add(leaf2);
    hinge.userData.leaf2 = leaf2;
    hinge.userData.leafW = leafW;
  }
  leaf.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  hinge.add(leaf);
  if (twin) hinge.userData.leaf1 = leaf;

  const hb = hitBox(DOOR_W + 0.2, DOOR_H + 0.1, 0.4);
  hb.position.set(slide ? 0 : 1.0, DOOR_H / 2, 0);
  hinge.add(hb);
  v.hit.push(hb);

  v.moving = hinge;
  // 문틀 밑이 곧 바닥이다. `def.pos[1]` 은 문의 「중심 높이」로 적혀 있지만
  // 문짝은 바닥에서 올라오는 물건이라 그 값을 쓰지 않는다
  v.group.position.y = v.floorY ?? 0;

  /**
   * 닫힌 문을 막는 상자.
   *
   * **폭을 문짝에 맞춘다.** 1.0(반폭 기준 2.0m)으로 박혀 있어서 문구멍(1.1)의
   * 두 배가 부딪혔고, 문 양옆 벽 안쪽까지 침범했다. 대기방에서 문을 못 열면
   * 사방이 막힌 것처럼 느껴진 이유다.
   */
  /**
   * 닫힌 문을 막는 상자 — **문짝이 실제로 서 있는 자리**에 둔다.
   *
   * 폭이 1.0(=2m)으로 박혀 있어서 구멍(1.1)의 두 배가 부딪혔고, 자리도
   * 경첩 기준이라 열어도 엉뚱한 데가 막혀 있었다.
   */
  const half = DOOR_W / 2;
  const cx = def.pos[0] + (slide ? dir.x * half : 0);
  const cz = def.pos[2] + (slide ? dir.z * half : 0);
  const horizontal = Math.abs(Math.cos(rot)) > 0.5;
  v.collider = {
    minX: cx - (horizontal ? half : WALL_T / 2),
    maxX: cx + (horizontal ? half : WALL_T / 2),
    minZ: cz - (horizontal ? WALL_T / 2 : half),
    maxZ: cz + (horizontal ? WALL_T / 2 : half),
    enabled: true,
  };
}

function buildDrawer(def: PropDef, v: PropView) {
  const size = def.size ?? [0.9, 0.2, 0.1];
  // 서랍은 문·조사물과 달리 그룹을 안 돌리고 있었다. 옆벽에 붙은 캐비닛의 앞면이
  // 벽을 보고 열리는 이유가 이것이다.
  v.group.rotation.y = -(def.rot ?? 0);
  const slider = new THREE.Group();
  v.group.add(slider);

  const face = new THREE.Mesh(
    new THREE.BoxGeometry(size[0], size[1], 0.06),
    new THREE.MeshStandardMaterial({ color: '#3d2c22', roughness: 0.7 }),
  );
  face.castShadow = true;
  slider.add(face);

  const handle = new THREE.Mesh(
    new THREE.BoxGeometry(size[0] * 0.4, 0.03, 0.05),
    new THREE.MeshStandardMaterial({ color: '#b08d4a', roughness: 0.35, metalness: 0.7 }),
  );
  handle.position.z = 0.05;
  slider.add(handle);

  // 열렸을 때 보이는 내부
  const box = new THREE.Mesh(
    new THREE.BoxGeometry(size[0] - 0.06, size[1] - 0.03, 0.42),
    new THREE.MeshStandardMaterial({ color: '#241a14', roughness: 0.95 }),
  );
  box.position.z = -0.24;
  slider.add(box);

  const hb = hitBox(size[0] + 0.2, size[1] + 0.25, 0.3);
  slider.add(hb);
  v.hit.push(hb);
  v.moving = slider;
  slider.userData.axis = new THREE.Vector3(0, 0, 1);
}

/**
 * **덮개 — 카탈로그 조각이 통째로 움직인다.** (2026-09-09)
 * 서랍(buildDrawer)은 상자를 즉석에서 그리지만, 재받이·상자 플랩·서류함 서랍은 카탈로그에
 * 제대로 만든 조각이 있다. 그 조각을 그대로 올리고 열림값으로 `slide`/`swing` 을 먹인다.
 * 히트박스는 조각의 실측 부피(`fit`)에서 잡는다 — 손으로 찍은 크기는 조각과 어긋난다.
 */
/**
 * **이미 놓인 조각의 부품 하나를 잡는다** (`attach` + `part`).
 * 서랍장처럼 움직이는 부품이 여럿인 조각은, 조각을 프롭마다 다시 그리면 넷이 겹친다.
 * 조각은 한 번만 그려져 있고 프롭은 그중 한 부품만 맡는다(2026-09-11).
 */
/** 부품이 아직 없어 못 붙인 덮개 프롭 — 조각 키 → 프롭들. glb.ts 가 `open:*` 부품을 만들면 `__glbPartsReady` 로 알린다 */
const pendingAttach = new Map<string, PropView[]>();
let pendingRoot: THREE.Object3D | null = null;
(globalThis as unknown as { __glbPartsReady?: (pieceKey: string) => void }).__glbPartsReady = (pieceKey: string) => {
  const list = pendingAttach.get(pieceKey); if (!list || !pendingRoot) return;
  const left = list.filter((v) => !attachToPart(v.def, v, pendingRoot!));
  if (left.length) pendingAttach.set(pieceKey, left); else pendingAttach.delete(pieceKey);
};

function attachToPart(def: PropDef, v: PropView, root: THREE.Object3D): boolean {
  if (!def.attach || !def.part) return false;
  let host: THREE.Object3D | null = null;
  root.traverse((o) => { if (!host && o.userData.pieceKey === def.attach) host = o; });
  if (!host) { console.warn(`lid ${def.id}: 붙을 조각 '${def.attach}' 를 못 찾았다`); return false; }
  const h = host as THREE.Object3D;
  const obj = h.getObjectByName(`open:${def.part}`);
  if (!obj) return false;   // GLB 조각이면 모델이 붙은 뒤 __glbPartsReady 로 다시 온다
  const pieceId = h.userData.pieceId as string | undefined;
  const spec = pieceId ? openSpecFor(pieceId, h.userData.pieceDims as Record<string, number> | undefined, def.part) : undefined;   // 변형 치수에 맞춘 경첩
  const pivot = new THREE.Vector3(...(spec?.pivot ?? [0, 0, 0]));
  /* 손이 닿을 상자는 **부품을 뺴기 전에** 잰다 — obj 가 아직 h 바로 아래 있을 때라
   * bb 와 h.worldToLocal 이 같은 matrixWorld 를 써서 정확히 상쇄된다. 예전엔 경첩에
   * 단 뒤에 재서 상자가 pivot 만큼 어긋났다 (2026-09-17).
   * **조각의 matrixWorld 부터 새로 굳힌다** (2026-09-23, Chris: 「위에 덮힌 파츠는 안 잡힌다」).
   * `Box3.setFromObject` 는 부모를 갱신하지 않아 첫 렌더 전엔 단위행렬로 재는데
   * `worldToLocal` 은 스스로 갱신해 진짜 행렬로 되돌린다 — 조각마다 **첫 부품**만 좌표를 두 번
   * 뒤집어 상자가 월드 원점 근처로 날아갔다. 뚜껑 하나짜리 상자는 아예 못 집었다. */
  h.updateWorldMatrix(true, false);
  const bb = new THREE.Box3().setFromObject(obj);
  const sz = bb.getSize(new THREE.Vector3()), ctr = bb.getCenter(new THREE.Vector3());
  const hinge = new THREE.Group();
  hinge.position.copy(pivot);
  /* **이 부품이 누구 것인지 표시해 둔다** — 에디터에서 서랍을 클릭하면 부모(서랍장)를 먼저
   * 만나 서랍장이 잡혔다(2026-09-12, Chris: 「세 칸 열리는 곳은 어딜 어케 조정함」).
   * 서랍 한 칸만 열려면 그 칸을 집을 수 있어야 한다. */
  hinge.userData.propId = def.id;
  h.add(hinge);
  obj.position.sub(pivot);
  hinge.add(obj);
  v.openParts = [{ obj: hinge, swing: spec?.swing ?? def.swing, axis: spec?.axis, slide: spec?.slide ? new THREE.Vector3(...spec.slide) : (def.slide ? new THREE.Vector3(...def.slide) : undefined), gather: spec?.gather, base: hinge.position.clone() }];
  // 얇은 서랍 앞판·커튼 자리표시자도 잡히게 최소 치수를 준다
  const hb = hitBox(Math.max(sz.x, 0.2) + 0.06, Math.max(sz.y, 0.12) + 0.06, Math.max(sz.z, 0.2) + 0.06);
  /* **상자는 경첩에 단다** (2026-09-17, Chris: 「싹다 상호작용」) — 부품이 밀리면
   * (미닫이창 짝·커튼 폭·서랍) 상자도 같이 가야 옮겨진 자리에서 다시 집을 수 있다. */
  h.worldToLocal(ctr); hb.position.copy(ctr).sub(pivot);
  hinge.add(hb);
  v.hit.push(hb);
  return true;
}

function buildLid(def: PropDef, v: PropView, renderer: THREE.WebGLRenderer) {
  const piece = def.piece ? PIECE_BY_ID[def.piece] : undefined;
  /* **덮개의 rot 은 조각 규약(+)이다.** 다른 프롭은 `−rot` 인데, 덮개는 몸통 조각과 같은 값을
   * 쓰는 게 맞다 — 배치 솔버가 `near` 프롭에 조각의 rot 을 그대로 주고, 손 배치도 몸통과 같은 수를
   * 적으면 된다. 2026-09-09: −rot + π 회전으로 맞추려다 재받이가 난로 **뒤로** 빠졌다. */
  v.group.rotation.y = def.rot ?? 0;
  const mover = new THREE.Group();
  v.group.add(mover);
  if (!piece) {
    console.warn(`lid ${def.id}: piece '${def.piece}' 없음`);
    return;
  }
  const built = buildPiece(piece, renderer);
  built.group.traverse((o) => { if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).castShadow = true; });

  /* **조각이 제 뚜껑을 안고 있으면** (`Piece.openable`) 조각을 통째로 세우고 그 부품만 움직인다
   * (2026-09-11, Chris: 「일체로 만들어서」). 예전처럼 덮개 조각을 따로 얹지 않으므로
   * 몸통과 뚜껑이 따로 놀 일이 없다 — 상자를 옮기면 뚜껑이 같이 간다. */
  if (piece.openable?.length) {
    v.group.add(built.group);
    v.openParts = [];
    for (const spec0 of piece.openable) {
      const spec = openSpecFor(piece.id, undefined, spec0.part) ?? spec0;   // 변형 치수에 맞춘 경첩
      const obj = built.group.getObjectByName(`open:${spec.part}`);
      if (!obj) { console.warn(`openable ${def.id}: '${spec.part}' 부품이 없다 — w.movable 로 감쌌나?`); continue; }
      /* 젖힘은 **축을 옮겨서** 한다. 부품 지오메트리는 조각 원점 기준이므로,
       * 축 자리로 그룹을 옮기고 부품을 그만큼 되민다. 안 그러면 제자리에서 빙 돈다. */
      const pivot = new THREE.Vector3(...(spec.pivot ?? [0, 0, 0]));
      const hinge = new THREE.Group();
      hinge.position.copy(pivot);
      built.group.add(hinge);
      obj.position.sub(pivot);
      hinge.add(obj);
      v.openParts.push({ obj: hinge, swing: spec.swing, axis: spec.axis, slide: spec.slide ? new THREE.Vector3(...spec.slide) : undefined, gather: spec.gather, base: hinge.position.clone() });
    }
    const sz2 = built.fit.getSize(new THREE.Vector3());
    const ctr2 = built.fit.getCenter(new THREE.Vector3());
    const hb2 = hitBox(Math.max(sz2.x, 0.2) + 0.1, Math.max(sz2.y, 0.12) + 0.1, Math.max(sz2.z, 0.2) + 0.1);
    hb2.position.copy(ctr2);
    v.group.add(hb2);
    v.hit.push(hb2);
    return;
  }
  // 조각을 그대로 올린다 — 정면 −Z, `slide` 도 조각 규약(앞으로 빠지면 −Z)
  // `pivot` 이 있으면 회전축이 그 자리에 오도록 조각을 반대로 민다 (플랩은 뒤 모서리에서 젖힌다)
  if (def.pivot) built.group.position.set(-def.pivot[0], -def.pivot[1], -def.pivot[2]);
  mover.add(built.group);
  const sz = built.fit.getSize(new THREE.Vector3());
  const ctr = built.fit.getCenter(new THREE.Vector3());
  // 얇은 덮개는 손이 닿을 두께가 없다 — 최소 12cm 는 준다
  const hb = hitBox(Math.max(sz.x, 0.2) + 0.1, Math.max(sz.y, 0.12) + 0.1, Math.max(sz.z, 0.2) + 0.1);
  hb.position.copy(ctr);
  mover.add(hb);
  v.hit.push(hb);
  v.moving = mover;
  if (def.slide) mover.userData.slide = new THREE.Vector3(...def.slide);
  if (def.swing) mover.userData.swing = def.swing;
}

function buildPickup(def: PropDef, v: PropView) {
  const color = def.color ?? '#d0d0d0';
  const item = new THREE.Group();
  const metal = (c: string, rough = 0.3) =>
    new THREE.MeshStandardMaterial({ color: c, roughness: rough, metalness: 0.75 });

  if (def.id === 'key_brass') {
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.16, 8), metal(color));
    shaft.rotation.z = Math.PI / 2;
    item.add(shaft);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.035, 0.011, 8, 16), metal(color));
    ring.position.x = -0.1;
    item.add(ring);
    for (let i = 0; i < 2; i++) {
      const t = new THREE.Mesh(new THREE.BoxGeometry(0.016, 0.04, 0.014), metal(color));
      t.position.set(0.05 + i * 0.03, -0.025, 0);
      item.add(t);
    }
  } else if (def.id === 'shovel') {
    /* 제설용 삽 — 눕혀 놓은 것. 자루는 X 축으로 길게, 날은 +X 끝에 얕게 깔린다.
     * 나무 자루 + 철 날 + T 손잡이 조합이면 멀리서도 「삽」으로 읽힌다. */
    const wood = new THREE.MeshStandardMaterial({ color: '#8a6a42', roughness: 0.8 });
    const steel = metal('#7a8288', 0.45);
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 1.05, 8), wood);
    shaft.rotation.z = Math.PI / 2;
    shaft.position.set(-0.1, 0.03, 0);
    item.add(shaft);
    /* 날 — 넓적한 철판을 살짝 앞으로 숙인다 */
    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.014, 0.34), steel);
    blade.position.set(0.56, 0.02, 0);
    blade.rotation.z = -0.08;
    item.add(blade);
    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.12, 8), steel);
    neck.rotation.z = Math.PI / 2;
    neck.position.set(0.4, 0.03, 0);
    item.add(neck);
    /* T 손잡이 — 자루 반대 끝의 가로 막대 */
    const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 0.22, 8), wood);
    grip.rotation.x = Math.PI / 2;
    grip.position.set(-0.66, 0.045, 0);
    item.add(grip);
    const gripBar = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.03), wood);
    gripBar.position.set(-0.62, 0.03, 0);
    item.add(gripBar);
  } else if (def.id === 'wine_glass') {
    // transmission 금지 — furniture/slots.ts 'glass' 참고(프레임마다 씬을 한 번 더 그린다)
    const glass = new THREE.MeshStandardMaterial({
      color: '#e8dff0',
      roughness: 0.08,
      metalness: 0.1,
      transparent: true,
      opacity: 0.4,
    });
    const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.022, 0.09, 16, 1, true), glass);
    cup.position.y = 0.09;
    item.add(cup);
    const wine = new THREE.Mesh(
      new THREE.CylinderGeometry(0.037, 0.021, 0.035, 16),
      new THREE.MeshStandardMaterial({ color, roughness: 0.25 }),
    );
    wine.position.y = 0.065;
    item.add(wine);
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, 0.07, 8), glass);
    stem.position.y = 0.015;
    item.add(stem);
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.006, 16), glass);
    base.position.y = -0.02;
    item.add(base);
  } else {
    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.005, 0.035), metal(color, 0.15));
    item.add(blade);
    const grip = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.018, 0.026),
      new THREE.MeshStandardMaterial({ color: '#2c2119', roughness: 0.85 }),
    );
    grip.position.x = -0.15;
    item.add(grip);
  }

  item.traverse((c) => {
    if (c instanceof THREE.Mesh) c.castShadow = true;
  });
  v.group.add(item);

  // 미발견 아이템은 눈에 띄어야 한다. 다만 아이템마다 PointLight 를 달면
  // 라이트 개수가 늘어 '모든 픽셀'의 셰이딩 비용이 오른다 — emissive 로 대신한다.
  item.traverse((c) => {
    if (c instanceof THREE.Mesh && c.material instanceof THREE.MeshStandardMaterial) {
      c.material.emissive = new THREE.Color(color);
      c.material.emissiveIntensity = 0.35;
    }
  });

  const hb = hitBox(0.34, 0.34, 0.34);
  hb.position.y = 0.06;
  v.group.add(hb);
  v.hit.push(hb);
  v.moving = item;
}

function buildSwitch(def: PropDef, v: PropView) {
  v.group.rotation.y = -(def.rot ?? 0);
  const plate = new THREE.Mesh(
    new THREE.BoxGeometry(0.05, 0.19, 0.13),
    new THREE.MeshStandardMaterial({ color: '#e6e2da', roughness: 0.5 }),
  );
  v.group.add(plate);
  const lever = new THREE.Mesh(
    new THREE.BoxGeometry(0.03, 0.075, 0.05),
    new THREE.MeshStandardMaterial({ color: '#c9c4ba', roughness: 0.45 }),
  );
  lever.position.x = 0.035;
  v.group.add(lever);

  const hb = hitBox(0.3, 0.34, 0.34);
  v.group.add(hb);
  v.hit.push(hb);
  v.moving = lever;
}

/**
 * 시신 — **피해자 모델을 누운 자세로 굳혀 세운다.**
 *
 * ## 캡슐을 먼저 만들지 않는다
 *
 * 전에는 캡슐과 구로 사람 모양을 세워 두고 모델이 도착하면 `visible = false`
 * 로 감췄다. 그런데 감추는 판정(`hasSkin`)이 어긋나면 **도형과 모델이 그대로
 * 겹쳐** 선다. 실제로 본당에 그 상태로 누워 있었다.
 *
 * 가구 쪽에서 같은 사고를 이미 겪었고(`furniture/index.ts` 참고) 결론이 같다 —
 * **감추는 것으로는 못 막는다. 애초에 만들지 않고, 실패했을 때만 만든다.**
 *
 * 분필 윤곽과 조사 히트박스는 모델과 무관하게 남는다. 히트박스는 상호작용의
 * 소유자이고, 윤곽은 「시신이 여기 있었다」는 표시다.
 */
/** 붙박이장에 밀어 넣은 시신 — 무릎을 가슴 쪽으로, 팔은 몸 앞으로, 등은 굽힘 */
export const CORPSE_POSE: import('./motion').Pose = {
  LeftUpLeg: { flex: 1.7, abd: 0.15 }, RightUpLeg: { flex: 1.45, abd: 0.05 },
  LeftLeg: { flex: 2.0 }, RightLeg: { flex: 1.85 },
  LeftFoot: { flex: 0.3 }, RightFoot: { flex: 0.2 },
  LeftArm: { down: 1.2, fwd: 0.9 }, RightArm: { down: 1.35, fwd: 0.5 },
  LeftForeArm: { flex: 1.9 }, RightForeArm: { flex: 1.5 },
  Spine: { flex: 0.35 }, Spine1: { flex: 0.3 }, Spine2: { flex: 0.25 },
  Neck: { flex: 0.35 }, Head: { flex: 0.2, side: 0.2 },
};

function buildBody(def: PropDef, v: PropView) {
  /** 모델이 끝내 안 오면 이걸로 세운다. 첫 장면이 비는 것보다 낫다 */
  const capsules = () => {
    const cloth = new THREE.MeshStandardMaterial({ color: '#4b4a52', roughness: 0.9 });
    const skin = new THREE.MeshStandardMaterial({ color: '#c9a68c', roughness: 0.8 });
    const parts: THREE.Mesh[] = [];
    const put = (m: THREE.Mesh) => {
      m.castShadow = true;
      parts.push(m);
      v.group.add(m);
    };
    const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.19, 0.5, 6, 12), cloth);
    torso.rotation.z = Math.PI / 2;
    torso.position.y = 0.19;
    put(torso);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.135, 16, 12), skin);
    head.position.set(-0.52, 0.15, 0.05);
    put(head);
    const legs = new THREE.Mesh(new THREE.CapsuleGeometry(0.13, 0.62, 6, 10), cloth);
    legs.rotation.z = Math.PI / 2;
    legs.position.set(0.62, 0.14, 0.06);
    put(legs);
    const armL = new THREE.Mesh(new THREE.CapsuleGeometry(0.075, 0.42, 6, 8), cloth);
    armL.rotation.set(0, 0, Math.PI / 2.6);
    armL.position.set(-0.16, 0.11, 0.32);
    put(armL);
    const armR = new THREE.Mesh(new THREE.CapsuleGeometry(0.075, 0.4, 6, 8), cloth);
    armR.rotation.set(0, 0, -Math.PI / 2.2);
    armR.position.set(-0.1, 0.11, -0.3);
    put(armR);
    return parts;
  };

  // 분필 윤곽 — 시신이 무엇으로 그려지든 남는다
  const outline = new THREE.Mesh(
    new THREE.RingGeometry(1.05, 1.14, 40),
    new THREE.MeshBasicMaterial({
      color: '#f2ede0',
      transparent: true,
      opacity: 0.62,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  outline.rotation.x = -Math.PI / 2;
  outline.renderOrder = 2;
  outline.position.y = -0.055;
  v.group.add(outline);

  void (async () => {
    const { buildAvatar } = await import('./avatar');
    /**
     * **피해자는 사건이 정한다.** `'han'` 이 박혀 있어서 초치성 본당에
     * 사흘의 피해자가 누워 있었다.
     */
    const a = buildAvatar(ep().victim, new THREE.Color('#ffffff'));
    v.group.userData.avatar = a;   // 진단용
    a.group.position.y = 0;
    /**
     * **몸을 눕힐 바닥은 월드 좌표다 — 0 이 아니다.**
     *
     * `0` 이 박혀 있었다. 사흘은 마당이 평지(y=0)라 맞았지만, 초치성 본당은
     * 40cm 기단 위다. 그래서 시신이 마루 속으로 40cm 파묻혔고, 벌린 팔과
     * 무릎만 바닥 위로 삐져나와 **구겨진 천 조각처럼** 보였다.
     *
     * `v.group` 은 `def.pos[1]` 만큼 더 올라가 있으므로 그 몫을 빼야 그
     * 자리의 마루 높이가 된다.
     */
    const floor = (v.floorY ?? 0);
    v.group.add(a.group);
    // 머리 방향은 프롭의 rot 가 정한다 (rot 0 = −Z). 안 적었으면 붙박이장 안쪽(−X)
    const head = def.rot !== undefined
      ? new THREE.Vector3(-Math.sin(def.rot), 0, -Math.cos(def.rot))
      : new THREE.Vector3(-1, 0, 0);
    // v7 시신은 붙박이장에 밀어 넣은 몸 — 웅크리고 모로 눕는다
    // 관절 각도로 세운 웅크린 자세 — 한계 안에서만 움직인다 (motion.JOINTS).
    // **서버 포즈 저장소에 `corpse` 가 있으면 그것을 쓴다** — 포저(dev)에서 저장한 자세가 코드 기본값을 이긴다
    const saved = ep().poses?.corpse;
    a.freezeDead(floor, head, { joints: saved ?? CORPSE_POSE, roll: 1.45 });
    {
      // **장 안은 방 등이 못 든다.** 두 짝을 열어도 시신이 검은 덩어리였다(프리뷰 B2 컷).
      // 발견의 순간이 읽히려면 몸이 보여야 한다 — 장 앞 위쪽에 약한 등 하나. 그림자는 안 만든다
      // 물리 단위(칸델라)다 — 촬영 플래시가 5.5 인데 1.6 으론 티도 안 났다(프리뷰 재확인). 장 안 천장에 단다
      const lamp = new THREE.PointLight('#e8dcc4', 7, 3.2, 1.6);
      lamp.position.set(0.15, 1.85, 0);
      v.group.add(lamp);
    }
    // 모델이 붙었는지는 **아바타가 알려 준다.** 여기서 스킨 메시를 훑어봐야
    // 소용없다 — 모델은 이 시점 이후에 도착한다
    if (!(await a.ready)) {
      console.warn('[시신] 모델이 안 붙었다 — 캡슐로 대체한다');
      capsules();
    }
  })().catch((e) => {
    console.warn('[시신] 모델 로드 실패 — 캡슐로 남는다', e);
    capsules();
  });

  const hb = hitBox(1.9, 1.0, 1.2);
  hb.position.y = 0.2;
  v.group.add(hb);
  v.hit.push(hb);
}

/**
 * **러그 밑창 발자국 스프라이트** (사흘 v8 G12) — 캔버스에 발 앞쪽(러그 무늬)과
 * 힐을 따로 그린 알파 텍스처. 평면 메시에 입혀 눈 위의 자국으로 쓴다.
 * 한 번 만들어 캐시한다.
 */
let _trackTex: THREE.CanvasTexture | null = null;
function bootTrackTexture(): THREE.CanvasTexture {
  if (_trackTex) return _trackTex;
  const c = document.createElement('canvas');
  c.width = 64; c.height = 128;
  const g = c.getContext('2d')!;
  g.clearRect(0, 0, 64, 128);
  g.fillStyle = '#ffffff';
  // 발 앞쪽 — 타원에 가로 러그 5줄
  g.save();
  g.beginPath(); g.ellipse(32, 40, 24, 38, 0, 0, Math.PI * 2); g.clip();
  for (let i = 0; i < 5; i++) g.fillRect(6, 12 + i * 13, 52, 8);
  g.restore();
  // 힐 — 작은 타원 + 러그 2줄
  g.save();
  g.beginPath(); g.ellipse(32, 106, 17, 16, 0, 0, Math.PI * 2); g.clip();
  for (let i = 0; i < 2; i++) g.fillRect(14, 98 + i * 14, 36, 9);
  g.restore();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  _trackTex = t;
  return t;
}

/**
 * 조사 대상의 생김새.
 *
 * 예전에는 무엇이든 '튀어나온 책 한 권'이었다. 저택 데모에서는 통했지만,
 * 혈흔·끌린 자국·열린 창문·쟁반 세 개가 전부 같은 빨간 책으로 보이면
 * 크라임씬이 성립하지 않는다. 단서는 눈으로 읽혀야 한다.
 *
 * `marker` 는 지오메트리를 만들지 않는다 — 사물함·칠판·난로처럼 가구가 이미
 * 그 자리에 있는 것들이다. 가구 위에 책을 얹으면 그게 더 이상하다.
 */
function buildExamLook(def: PropDef, v: PropView): boolean {
  const add = (m: THREE.Mesh) => {
    m.castShadow = true;
    m.receiveShadow = true;
    v.group.add(m);
    return m;
  };

  switch (def.look) {
    case 'marker':
      return true;

    case 'stain': {
      // 고인 피 — 가운데가 짙고 가장자리로 번진다
      for (const [r, c] of [
        [0.31, '#2a0c06'],
        [0.21, '#360f08'],
        [0.12, '#43150b'],
      ] as const) {
        const m = add(
          new THREE.Mesh(
            new THREE.CircleGeometry(r, 22),
            new THREE.MeshStandardMaterial({ color: c, roughness: 0.25, side: THREE.DoubleSide }),
          ),
        );
        m.rotation.x = -Math.PI / 2;
        m.position.y = 0.004 + (0.44 - r) * 0.003;
      }
      return true;
    }

    case 'crawl': {
      // 기어간 자국 — **끊긴다**. 손이 닿은 자리마다 찍히고 사이가 빈다
      const hand = new THREE.MeshStandardMaterial({ color: '#2e0e07', roughness: 0.6, side: THREE.DoubleSide });
      for (let i = 0; i < 6; i++) {
        const t = i / 5;
        const m = add(new THREE.Mesh(new THREE.CircleGeometry(0.1 - t * 0.035, 14), hand));
        m.rotation.x = -Math.PI / 2;
        m.scale.set(1.25, 1, 0.78);
        m.position.set((i - 2.5) * 0.52, 0.005, i % 2 ? 0.16 : -0.16);
      }
      return true;
    }

    case 'drag': {
      // 끌린 자국 — **안 끊긴다**. 발뒤꿈치가 두 줄로 길게 남는다
      const streak = new THREE.MeshStandardMaterial({ color: '#33100a', roughness: 0.55, side: THREE.DoubleSide });
      for (const off of [-0.13, 0.13]) {
        const m = add(new THREE.Mesh(new THREE.PlaneGeometry(2.7, 0.11), streak));
        m.rotation.x = -Math.PI / 2;
        m.position.set(0, 0.006, off);
      }
      const wash = add(
        new THREE.Mesh(
          new THREE.PlaneGeometry(2.7, 0.42),
          new THREE.MeshStandardMaterial({ color: '#240905', roughness: 0.7, side: THREE.DoubleSide }),
        ),
      );
      wash.rotation.x = -Math.PI / 2;
      wash.position.y = 0.004;
      return true;
    }

    case 'window': {
      // 창틀은 world.ts 가 개구부마다 세운다. 여기서는 '열려 있다'는 사실만 만든다 —
      // 안쪽으로 젖혀진 창짝 하나, 그리고 창턱에 들이친 눈.
      // 창짝은 틀 두께만 있고 가운데는 유리다. 통판으로 만들면 문짝처럼 보인다
      const sashMat = new THREE.MeshStandardMaterial({ color: '#5b6268', roughness: 0.6, metalness: 0.3 });
      const sash = new THREE.Group();
      for (const [w, h, x, y] of [
        [0.7, 0.06, 0, 0.5],
        [0.7, 0.06, 0, -0.5],
        [0.06, 1.06, -0.32, 0],
        [0.06, 1.06, 0.32, 0],
      ] as const) {
        const bar = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.04), sashMat);
        bar.position.set(x, y, 0);
        bar.castShadow = true;
        sash.add(bar);
      }
      const pane = new THREE.Mesh(
        new THREE.PlaneGeometry(0.6, 0.96),
        new THREE.MeshStandardMaterial({   // transmission 금지 — furniture/slots.ts 'glass' 참고
          color: '#9fb4c8',
          roughness: 0.16,
          metalness: 0.14,
          transparent: true,
          opacity: 0.28,
          side: THREE.DoubleSide,
        }),
      );
      sash.add(pane);
      sash.position.set(0.58, 0, 0.28);
      sash.rotation.y = -0.8;
      v.group.add(sash);
      // 창턱에 들이친 눈 — 사흘 동안 열려 있었다
      const snowSill = add(
        new THREE.Mesh(
          new THREE.BoxGeometry(0.66, 0.025, 0.13),
          new THREE.MeshStandardMaterial({ color: '#93a3b4', roughness: 0.95 }),
        ),
      );
      snowSill.position.set(-0.36, -0.605, 0.1);
      return true;
    }

    case 'papers': {
      for (let i = 0; i < 5; i++) {
        const sheet = add(
          new THREE.Mesh(
            new THREE.BoxGeometry(0.3, 0.006, 0.42),
            new THREE.MeshStandardMaterial({ color: i % 2 ? '#c3bca9' : '#b8b09d', roughness: 0.95 }),
          ),
        );
        sheet.position.y = 0.007 * i;
        sheet.rotation.y = (i - 2) * 0.05;
      }
      return true;
    }

    case 'trays': {
      // 쟁반 세 개. 이 시나리오에서는 개수가 곧 증거다
      for (let i = 0; i < 3; i++) {
        const tray = add(
          new THREE.Mesh(
            new THREE.BoxGeometry(0.42, 0.022, 0.3),
            new THREE.MeshStandardMaterial({ color: '#9ba3ad', roughness: 0.35, metalness: 0.5 }),
          ),
        );
        tray.position.set(i * 0.02 - 0.02, i * 0.035, i * 0.014 - 0.014);
        tray.rotation.y = (i - 1) * 0.06;
      }
      return true;
    }

    case 'phone': {
      const body = add(
        new THREE.Mesh(
          new THREE.BoxGeometry(0.075, 0.012, 0.152),
          new THREE.MeshStandardMaterial({ color: '#17191d', roughness: 0.35, metalness: 0.5 }),
        ),
      );
      body.rotation.y = 0.35;
      const screen = add(
        new THREE.Mesh(
          new THREE.BoxGeometry(0.066, 0.002, 0.138),
          new THREE.MeshStandardMaterial({ color: '#0b0d10', roughness: 0.12, metalness: 0.1 }),
        ),
      );
      screen.rotation.y = 0.35;
      screen.position.y = 0.007;
      return true;
    }

    case 'tracks': {
      // 눈 위의 발자국. 새벽마다 났다는 그 자국이다 — 이제 복도가 아니라 마당이다
      const print = new THREE.MeshStandardMaterial({ color: '#7c8b9c', roughness: 0.95 });
      for (let i = 0; i < 10; i++) {
        const m = add(new THREE.Mesh(new THREE.CircleGeometry(0.1, 12), print));
        m.rotation.x = -Math.PI / 2;
        m.scale.set(1.0, 1, 1.9);
        m.position.set((i - 4.5) * 0.62, 0.004, i % 2 ? 0.17 : -0.17);
      }
      return true;
    }

    case 'tracks_pair': {
      /* **사흘 v8 G12 — 언덕길 발자국 두 벌** (평면 메시 + 알파 텍스처).
       *   ① 굵은 러그, 내리막 한 벌 — 강설 중에 내려간 범인. 위쪽(올라간 시각이
       *      아니라 내린 시각에 눈이 쌓였다)으로 갈수록 눈에 반쯤 메워져 흐릿하다.
       *   ② 작은 발, 오르내린 두 벌 — 강설이 그친 뒤 송이경. 또렷하다.
       * 로컬 +X 가 내리막이다 — prop 을 rot π/2 로 놓아 로컬 X 가 마을(남)을 본다.
       * 경사는 RAMPS.hill (0→3.6m / 18m) — 발자국 무리를 통째로 기울여 비탈에 붙인다. */
      const tex = bootTrackTexture();
      const slope = Math.atan(3.6 / 18);
      const pair = new THREE.Group();
      pair.rotation.z = -slope;   // 로컬 +X(내리막) 쪽 끝을 내린다
      v.group.add(pair);
      const put = (lx: number, lz: number, yawJitter: number, s: number, alpha: number) => {
        const m = new THREE.Mesh(
          new THREE.PlaneGeometry(0.13 * s, 0.31 * s),
          new THREE.MeshStandardMaterial({
            map: tex, transparent: true, opacity: alpha,
            color: '#54637a', roughness: 1, depthWrite: false,
            polygonOffset: true, polygonOffsetFactor: -1,
          }),
        );
        m.rotation.x = -Math.PI / 2;
        m.rotation.z = yawJitter;
        m.position.set(lx, 0.006, lz);
        m.receiveShadow = true;
        pair.add(m);
      };
      // ① 굵은 러그 — 내리막(=로컬 +X 방향으로 진행). 언덕 위(-X)쪽일수록 눈에 묻혀 옅다
      for (let i = 0; i < 9; i++) {
        const lx = -5.2 + i * 1.15;
        const fade = 0.18 + 0.55 * (i / 8);              // 위(-X)로 갈수록 옅게
        put(lx, -0.62 + (i % 2 ? 0.17 : -0.17), (i % 2 ? 1 : -1) * 0.07, 1.35, fade);
      }
      // ② 작은 발 — 오르막 레인 + 내리막 레인, 또렷
      for (let i = 0; i < 8; i++) {
        put(-4.9 + i * 1.25, 0.55 + (i % 2 ? 0.14 : -0.14), (i % 2 ? 1 : -1) * 0.06, 0.92, 0.8);
      }
      for (let i = 0; i < 8; i++) {
        put(-4.4 + i * 1.25, 1.05 + (i % 2 ? 0.14 : -0.14), (i % 2 ? 1 : -1) * 0.06, 0.92, 0.8);
      }
      return true;
    }

    case 'slippers': {
      for (const side of [-1, 1]) {
        const sole = add(
          new THREE.Mesh(
            new THREE.BoxGeometry(0.12, 0.035, 0.28),
            new THREE.MeshStandardMaterial({ color: '#3f4247', roughness: 0.9 }),
          ),
        );
        sole.position.set(side * 0.09, 0.018, side * 0.03);
        sole.rotation.y = side * 0.12;
      }
      return true;
    }
  }
  return false;
}

function buildExam(def: PropDef, v: PropView, renderer?: THREE.WebGLRenderer) {
  /* **조사물이 카탈로그 조각이면 그 조각을 그린다** (2026-09-09). 덮개 속 큰 머그 조각처럼
   * 「열면 보이는 물건」은 마커가 아니라 실물이어야 한다. rot 은 조각 규약(+) — 덮개와 같다 */
  if (def.piece && renderer && PIECE_BY_ID[def.piece]) {
    v.group.rotation.y = def.rot ?? 0;
    const built = buildPiece(PIECE_BY_ID[def.piece], renderer);
    built.group.traverse((o) => { if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).castShadow = true; });
    v.group.add(built.group);
    const sz = built.fit.getSize(new THREE.Vector3());
    const ctr = built.fit.getCenter(new THREE.Vector3());
    const hb = hitBox(Math.max(sz.x, 0.15) + 0.1, Math.max(sz.y, 0.1) + 0.1, Math.max(sz.z, 0.15) + 0.1);
    hb.position.copy(ctr);
    v.group.add(hb);
    v.hit.push(hb);
    return;
  }
  v.group.rotation.y = -(def.rot ?? 0);

  if (buildExamLook(def, v)) {
    const wide = def.look === 'crawl' || def.look === 'drag' || def.look === 'tracks' || def.look === 'tracks_pair';
    const box = hitBox(wide ? 6.4 : 1.0, def.look === 'marker' ? 2.6 : 0.8, def.look === 'marker' ? 2.6 : 1.0);
    v.group.add(box);
    v.hit.push(box);
    return;
  }

  // 튀어나온 책 한 권
  const book = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 0.26, 0.19),
    new THREE.MeshStandardMaterial({ color: '#7b2f3a', roughness: 0.8 }),
  );
  book.castShadow = true;
  v.group.add(book);
  const pages = new THREE.Mesh(
    new THREE.BoxGeometry(0.05, 0.23, 0.17),
    new THREE.MeshStandardMaterial({ color: '#ddd2b8', roughness: 0.9 }),
  );
  pages.position.x = -0.012;
  v.group.add(pages);

  const hb = hitBox(0.6, 0.9, 0.7);
  v.group.add(hb);
  v.hit.push(hb);
  v.moving = book;
}

function buildSafe(def: PropDef, v: PropView) {
  v.group.rotation.y = -(def.rot ?? 0);
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.9, 1.0, 0.8),
    new THREE.MeshStandardMaterial({ color: '#2e3338', roughness: 0.45, metalness: 0.6 }),
  );
  body.castShadow = true;
  body.receiveShadow = true;
  v.group.add(body);

  const hinge = new THREE.Group();
  hinge.position.set(-0.45, 0, 0.4);
  v.group.add(hinge);
  const door = new THREE.Mesh(
    new THREE.BoxGeometry(0.86, 0.96, 0.07),
    new THREE.MeshStandardMaterial({ color: '#3a4046', roughness: 0.4, metalness: 0.7 }),
  );
  door.position.x = 0.43;
  door.castShadow = true;
  hinge.add(door);

  const dial = new THREE.Mesh(
    new THREE.CylinderGeometry(0.13, 0.13, 0.05, 20),
    new THREE.MeshStandardMaterial({ color: '#c8b98a', roughness: 0.3, metalness: 0.85 }),
  );
  dial.rotation.x = Math.PI / 2;
  dial.position.set(0.5, 0.05, 0.06);
  hinge.add(dial);
  const notch = new THREE.Mesh(
    new THREE.BoxGeometry(0.02, 0.06, 0.02),
    new THREE.MeshStandardMaterial({ color: '#2a2a2a' }),
  );
  notch.position.set(0.5, 0.14, 0.07);
  hinge.add(notch);

  // 내부 (열리면 보임)
  const inner = new THREE.Mesh(
    new THREE.BoxGeometry(0.74, 0.84, 0.6),
    new THREE.MeshStandardMaterial({ color: '#15181b', roughness: 1 }),
  );
  inner.position.z = 0.05;
  v.group.add(inner);
  const doc = new THREE.Mesh(
    new THREE.BoxGeometry(0.3, 0.42, 0.02),
    new THREE.MeshStandardMaterial({ color: '#e8e0cc', roughness: 0.9 }),
  );
  doc.rotation.set(-0.1, 0.2, 0.06);
  doc.position.set(0, -0.2, 0.12);
  v.group.add(doc);

  const hb = hitBox(1.2, 1.3, 1.1);
  v.group.add(hb);
  v.hit.push(hb);
  v.moving = hinge;
  v.collider = {
    minX: def.pos[0] - 0.5,
    maxX: def.pos[0] + 0.5,
    minZ: def.pos[2] - 0.5,
    maxZ: def.pos[2] + 0.5,
    enabled: true,
  };
}

function buildBoard(v: PropView) {
  const cork = new THREE.Mesh(
    new THREE.BoxGeometry(2.6, 1.6, 0.08),
    new THREE.MeshStandardMaterial({ color: '#8a6a45', roughness: 0.95 }),
  );
  cork.receiveShadow = true;
  v.group.add(cork);
  const frame = new THREE.Mesh(
    new THREE.BoxGeometry(2.76, 1.76, 0.06),
    new THREE.MeshStandardMaterial({ color: '#2f241c', roughness: 0.8 }),
  );
  frame.position.z = -0.03;
  v.group.add(frame);

  const notes = new THREE.Group();
  notes.name = 'notes';
  notes.position.z = -0.05;
  v.group.add(notes);

  const hb = hitBox(2.8, 1.9, 0.7);
  v.group.add(hb);
  v.hit.push(hb);
}

// --- 상태 반영 ---

export function syncProps(views: Map<string, PropView>, world: WorldState, built: BuiltWorld, youId: string) {
  for (const [id, v] of views) {
    const st = world.objects[id] ?? {};
    const def = v.def;

    // **서랍·캐비닛 속 물건은 그게 열려야 보인다.**
    // 이 검사가 pickup 에만 걸려 있어서, 닫힌 캐비닛 앞에 서류 뭉치가 그냥
    // 떠 있었다 — 「열지도 않았는데 서류가 이미 튀어나와 있음」이 그것이다.
    // 종류를 안 가리고 건다. 여덟 개가 그렇게 노출돼 있었다
    if (def.hiddenIn) {
      // 열린 서랍 **또는 치워진 덮개**(pickup 이 taken). 서버 room.ts 와 같은 규칙
      const cover = world.objects[def.hiddenIn];
      v.group.visible = cover?.open === true || cover?.taken === true;
      if (!v.group.visible) continue;
    }

    switch (def.kind) {
      case 'door':
        v.target = st.open ? 1 : 0;
        if (v.collider) v.collider.enabled = !st.open;
        break;
      case 'drawer':
      case 'lid':
        v.target = st.open ? 1 : 0;
        break;
      case 'safe':
        v.target = st.solved ? 1 : 0;
        break;
      case 'switch': {
        v.target = st.on ? 1 : 0;
        if (def.lightRoom) setRoomLight(built, def.lightRoom, st.on !== false);
        break;
      }
      case 'pickup': {
        const held = st.taken === true;
        v.group.visible = !held;
        if (st.p) v.group.position.set(st.p[0], st.p[1] + 0.06, st.p[2]);
        else v.group.position.set(def.pos[0], (v.floorY ?? 0) + def.pos[1], def.pos[2]);
        // 행렬이 얼어 있다(freeze.ts) — 지금 갱신하지 않으면 내려놓은 물건이 몇 프레임 옛 자리에 보였다가 옮겨 간다
        v.group.updateMatrix();
        v.group.updateMatrixWorld(true);
        v.group.userData.heldByMe = held && st.by === youId;
        break;
      }
      case 'board':
        syncBoard(v, world);
        break;
      default:
        break;
    }
  }
}

let boardCount = -1;
/**
 * 칠판에 붙는 쪽지.
 *
 * **찾은 단서가 아니라 「걸린 카드」다.** 전에는 `world.clues` 를 그렸는데,
 * 그러면 아무도 발표하지 않은 것까지 벽에 붙어 있었다. 칠판은 발표의 결과지
 * 조사의 결과가 아니다.
 */
function syncBoard(v: PropView, world: WorldState) {
  const clues = world.board ?? [];
  if (clues.length === boardCount) return;
  boardCount = clues.length;
  const notes = v.group.getObjectByName('notes') as THREE.Group | undefined;
  if (!notes) return;
  while (notes.children.length) notes.remove(notes.children[0]);

  clues.forEach((c, i) => {
    const col = i % 4;
    const row = Math.floor(i / 4);
    const paper = new THREE.Mesh(
      new THREE.PlaneGeometry(0.5, 0.62),
      new THREE.MeshStandardMaterial({ color: '#f0e9d8', roughness: 0.95, side: THREE.DoubleSide }),
    );
    paper.position.set(-0.95 + col * 0.63, 0.45 - row * 0.72, 0);
    paper.rotation.z = (Math.sin(i * 12.9898) * 0.06);
    notes.add(paper);

    const pin = new THREE.Mesh(
      new THREE.SphereGeometry(0.028, 8, 6),
      new THREE.MeshStandardMaterial({ color: '#c0392b', roughness: 0.4 }),
    );
    pin.position.set(paper.position.x, paper.position.y + 0.26, -0.03);
    notes.add(pin);

    // 제목 텍스트 대신 줄 표시 (폰트 로드 없이 가볍게)
    for (let k = 0; k < 4; k++) {
      const line = new THREE.Mesh(
        new THREE.PlaneGeometry(0.34 - k * 0.03, 0.022),
        new THREE.MeshBasicMaterial({ color: '#5b5347' }),
      );
      line.position.set(paper.position.x, paper.position.y + 0.1 - k * 0.1, -0.005);
      notes.add(line);
    }
    void c;
  });
}

/**
 * 매 프레임 애니메이션 보간.
 *
 * @returns 이 프레임에 실제로 트랜스폼이 바뀐 것의 개수. 그림자를 다시 구울지
 *   판단하는 데 쓴다 (main.ts) — 98개 중 실제로 움직이는 것은 보통 1~3개다.
 */
export function animateProps(views: Map<string, PropView>, dt: number, t: number) {
  let moved = 0;
  for (const v of views.values()) {
    if (Math.abs(v.current - v.target) > 0.001) {
      v.current += (v.target - v.current) * Math.min(1, dt * 8);
    } else {
      v.current = v.target;
    }
    /* 조각이 안고 있는 부품들 — 여럿이 **동시에** 움직인다(상자 양쪽 플랩) */
    if (v.openParts?.length) {
      if (v.applied !== v.current) {
        v.applied = v.current; moved++;
        for (const op of v.openParts) {
          /* 축을 안 적으면 z (좌우로 갈리는 플랩). 뚜껑·상자 덮개는 x, **여닫이 문짝은 y** 다
           * — 장롱·캐비닛 문은 세로 경첩에서 돌아간다(2026-09-12) */
          if (op.swing) { if (op.axis === 'x') op.obj.rotation.x = op.swing * v.current; else if (op.axis === 'y') op.obj.rotation.y = op.swing * v.current; else op.obj.rotation.z = op.swing * v.current; }
          if (op.slide) op.obj.position.copy(op.base).addScaledVector(op.slide, v.current);
          /* 걷어 모임 — 경첩(pivot = 부품 꼭대기)을 세로로 오므려 블라인드 슬랫이
           * 헤드레일에 겹쳐 모이게 한다 (2026-09-17, Chris) */
          if (op.gather != null) op.obj.scale.y = 1 - op.gather * v.current;
          // 행렬은 **움직인 프레임에만**, 제 가지만 — main.ts 가 이 노드와 조상을 얼려 두고 순회도 건너뛴다(freeze.ts).
          // 안 부르면 부품이 제자리에 굳는다
          op.obj.updateMatrix();
          op.obj.updateMatrixWorld(true);
        }
      }
      continue;
    }
    const m = v.moving;
    if (!m) continue;
    // 값이 그대로면 행렬을 다시 더럽힐 이유가 없다. 줍는 물건만 시간으로 떠 있어
    // 예외다. (applied 가 undefined 인 첫 프레임은 반드시 통과해 초기 자세를 잡는다)
    const settled = v.applied === v.current;
    if (settled && v.def.kind !== 'pickup') continue;
    v.applied = v.current;
    /* **줍는 물건이 도는 것은 「움직임」으로 세지 않는다** (2026-09-27). 이 수가 0 이 아니면 main.ts 가 그림자 맵 3장을
     * 초당 20번 다시 굽는다 — 삽 하나가 맵 어디에 있든 제자리에서 도는 바람에 혼자 가만히 서 있어도 그림자를 초당 18번
     * 구웠다(Wayne: 「조사하러 다니다 보니 팬이 엄청 돌고 발열이 심하다」). 도는 작은 물건의 그림자가 멈춰 있는 건 안 보인다 */
    if (!settled) moved++;

    switch (v.def.kind) {
      case 'door':
        if (ep().doorStyle === 'slide') {
          // **미닫이는 벽을 따라 미끄러진다.** 회전시키면 창호가 마당으로
          // 벌컥 열려서 그 순간 한옥이 아니게 된다.
          m.rotation.y = -(v.def.rot ?? 0);
          const leaf2 = m.userData.leaf2 as THREE.Object3D | undefined;
          const lw = (m.userData.leafW as number) ?? 1;
          if (leaf2) {
            /**
             * 두 짝은 **가운데서 갈라져 서로 반대로** 물러난다.
             *
             * 그룹째 밀면 두 짝이 한 덩어리로 옆으로 가서 한 짝짜리와 다를 게
             * 없다. 짝을 각자 움직여야 문이 「열린다」로 보인다.
             */
            leaf2.position.x = lw / 2 + v.current * lw;
            const leaf1 = m.userData.leaf1 as THREE.Object3D | undefined;
            if (leaf1) leaf1.position.x = -lw / 2 - v.current * lw;
          } else {
            const dir = m.userData.slideDir as THREE.Vector3 | undefined;
            const base = m.userData.slideBase as THREE.Vector3 | undefined;
            if (dir && base) m.position.copy(base).addScaledVector(dir, v.current * 0.98);
          }
        } else {
          m.rotation.y = -(v.def.rot ?? 0) - v.current * (Math.PI / 2.2);
        }
        break;
      case 'safe':
        m.rotation.y = -v.current * (Math.PI / 1.8);
        break;
      case 'drawer': {
        const axis = m.userData.axis as THREE.Vector3;
        m.position.copy(axis).multiplyScalar(v.current * 0.36);
        break;
      }
      case 'lid': {
        const sl = m.userData.slide as THREE.Vector3 | undefined;
        if (sl) m.position.copy(sl).multiplyScalar(v.current);
        const sw = m.userData.swing as number | undefined;
        if (sw) m.rotation.x = sw * v.current;
        break;
      }
      case 'switch':
        m.rotation.z = v.current ? 0.35 : -0.35;
        break;
      case 'pickup':
        m.rotation.y = t * 0.9;
        m.position.y = Math.sin(t * 1.8) * 0.02;
        break;
      case 'exam':
        break;
    }
    // 행렬은 **움직인 프레임에만**, 제 가지만 — main.ts 가 이 노드들과 조상을 얼려 두고 순회도 건너뛴다(freeze.ts)
    for (const n of [m, m.userData.leaf1 as THREE.Object3D | undefined, m.userData.leaf2 as THREE.Object3D | undefined]) {
      if (!n) continue;
      n.updateMatrix();
      n.updateMatrixWorld(true);
    }
  }
  return moved;
}

/**
 * 조준 하이라이트.
 * 원래 emissive 를 기억했다가 복원한다 — 그러지 않으면 아이템 자체 발광까지 지워진다.
 * 세기는 '보인다'를 넘지 않게 낮게. 크게 주면 조사 대상이 통째로 형광색이 된다.
 */
export function highlight(views: Map<string, PropView>, id: string | null) {
  for (const [pid, v] of views) {
    const on = pid === id;
    if (on) ownMaterials(v);
    for (const m of v.highlightable) {
      if (m.userData.baseEmissive === undefined) {
        m.userData.baseEmissive = m.emissive.getHex();
        m.userData.baseEmissiveIntensity = m.emissiveIntensity;
      }
      if (on) {
        m.emissive.setHex(0x1b3a63);
        m.emissiveIntensity = 0.22;
      } else {
        m.emissive.setHex(m.userData.baseEmissive as number);
        m.emissiveIntensity = m.userData.baseEmissiveIntensity as number;
      }
    }
  }
}
